import '../styles/index.scss';
import resourcesLoader from './common/resourcesLoader';
import Player from './entities/Player';
import Bullet from './entities/Bullet';
import Platform from './entities/Platform';
import {requestAnimFrame, randomInteger} from './common/commonFunctions';
import CanvasBackground from './common/CanvasBackground';
import mouseWatcher from './common/mouseWatcher';
import config from './common/config';
import EnemyFactory from './entities/EnemyFactory';

import smoke from '../images/smoke.png';
import sky from '../images/sky.png';
import city from '../images/city.png';
import playerImg from '../images/player.png';
import platform from '../images/platform.png';
import gun from '../images/gun.png';
import bullet from '../images/bullet.png';

let canvas;
let ctx;
let last;
let step = 1 / 60;
let dt = 0;
let now;
let lastScore = 0;
let score = 0;
let maxScore = 0;
let scoreEl;
let maxScoreEl;
let gameOverEl;
let canvasWidth;
let canvasHeight;

let isPause = false;
let isGameOver = false;
let backgrounds;
let lastShot = 0;
let lastEnemyAppearance = 0;
let player;
let enemyFactory;
let bullets;
let platforms;
let enemies;
const defaultSpeed = config.speed;

resourcesLoader.add({
    smoke,
    sky,
    city,
    player: playerImg,
    platform,
    gun,
    bullet
});
resourcesLoader.onReady(init);

function init() {
    let lsValue = localStorage.getItem("maxScore");
    if (lsValue !== null) {
        maxScore = lsValue;
    }
   
    canvas = document.getElementById('canvas');
    ctx = canvas.getContext('2d');
    scoreEl = document.getElementById('current-score');
    maxScoreEl = document.getElementById('max-score');
    gameOverEl = document.getElementById('game-over');
    maxScoreEl.textContent = maxScore;

    canvas.width = document.documentElement.clientWidth - 10;
    canvas.height =  document.documentElement.clientHeight >= 100? document.documentElement.clientHeight - 100 : document.documentElement.clientHeight;
    canvasWidth = canvas.width;
    canvasHeight = canvas.height;
    
    backgrounds = [
        new CanvasBackground(resourcesLoader.get('smoke'), canvasWidth, config.smokeBackgoundSpeed, 0, canvasHeight - resourcesLoader.get('smoke').height),
        new CanvasBackground(resourcesLoader.get('sky'), canvasWidth, config.skyBackgoundSpeed),
        new CanvasBackground(resourcesLoader.get('city'), canvasWidth, config.cityBackgoundSpeed, 0, canvasHeight - resourcesLoader.get('city').height)
    ];
    enemyFactory = new EnemyFactory(canvasHeight - config.platformHeight, canvasWidth);

    last = performance.now();
    assignHandlers();
    reset();
}

function assignHandlers () {
    window.onbeforeunload = function () {
        localStorage.setItem("maxScore", maxScore);
    };

    const exitBtn = document.getElementById('exit');
    exitBtn.onclick= () => {
        window.close();
    }

    const pauseBtn = document.getElementById('pause');
    pauseBtn.onclick = (e) => {
        if (isPause) {
            e.target.textContent = 'pause';
        } else {
            e.target.textContent = 'continue';
        }
        isPause = !isPause;
        e.target.blur();
    }

    const playAgainBtn = document.getElementById('play-again');
    playAgainBtn.onclick = () => {
        gameOverEl.classList.toggle('hidden');
        reset();
    }

    const newGameBtn = document.getElementById("new-game");
    newGameBtn.onclick = () => {
        const menu = document.getElementById("menu");
        menu.classList.toggle('hidden');
        engine();
    }
}

function engine() {
    now = performance.now();
    if (!isPause && !isGameOver) {
        dt = dt + Math.min(1, (now - last) / 1000);

        while (dt > step) {
            dt = dt - step;
            update();
        }
        render();
    }
    last = now;
    requestAnimFrame(engine);
}

function createPlatforms() {
    let x = 0;
    let y = canvasHeight - config.platformHeight;
    for (let i = 0, length = Math.floor(canvasWidth / config.platformWidth) + 2; i < length; i++) {
        platforms[i] = new Platform(resourcesLoader.get('platform'), x, y);
        x += config.platformWidth;
    }
}

function updateBullets() {
    for (let i = 0; i < bullets.length; i++) {
        bullets[i].update();
        if (bullets[i].x > canvasWidth || bullets[i].y > canvasHeight || bullets[i].x < 0 || bullets[i].y < 0 || bullets[i].y > platforms[0].y) {
            bullets.splice(i, 1);
            i--;
        }
    }

    if (mouseWatcher.isClick && performance.now() - lastShot > config.shotInterval) {
        const angle = player.gun.angle;
        const x = player.gun.x + Math.cos(angle) * player.gun.width;
        const y = player.gun.y + Math.sin(angle) * player.gun.height;

        bullets.push(new Bullet(resourcesLoader.get('bullet'), x, y, Math.cos(angle) * config.bulletSpeed, Math.sin(angle) * config.bulletSpeed, angle));

        lastShot = performance.now();
    }
}

function updateEnemies() {
    if (performance.now() - lastEnemyAppearance > config.enemyAppearanceInterval / config.speed) {
        switch (randomInteger(1, 3)) {
            case 1:
                enemies.push(enemyFactory.makeFlyingEnemy());
                break;
            case 2:
                enemies.push(enemyFactory.makeStandingEnemy());
                break;
            case 3:
                enemies.push(enemyFactory.makeWalkingEnemy());
                break;
            default:
                break;
        }
        lastEnemyAppearance = performance.now();
    }

    for (let i = 0; i < enemies.length; i++) {
        enemies[i].update();
        if (enemies[i].x < -enemies[i].width || enemies[i].y < -enemies[i].height) {
            enemies.splice(i, 1);
            i--;
        }
    }
}

function updatePlatforms() {
    for (let i = 0; i < platforms.length; i++) {
        platforms[i].update();
    }

    if (platforms[0].x <= -config.platformWidth) {
        platforms.shift();
        platforms.push(new Platform(resourcesLoader.get('platform'), platforms[0].x + config.platformWidth * platforms.length, canvasHeight - config.platformHeight));
    }
}

function checkCollision() {
    for (let i = 0; i < enemies.length; i++) {
        for (let j = 0; j < bullets.length; j++) {
            if (enemies[i].getMinDistance(bullets[j]) < Math.max(enemies[i].width, enemies[i].height) / 2 + bullets[j].width) {
                enemies[i].dy = -config.enemyInParadiseSpeed;
                enemies[i].dx = 0;
                bullets.splice(j, 1);
                j--;
                score++;
            }
        }
    }

    for (let i = 0; i < platforms.length; i++) {
        if (player.getMinDistance(platforms[i]) <= player.height / 2 + config.platformWidth / 2) {
            player.isJump = false;
            player.y = platforms[i].y - player.height;
            player.dy = 0;
        }
    }
    
    for (let i = 0; i < enemies.length; i++) {
        if (player.getMinDistance(enemies[i]) < player.width / 3 + enemies[i].width / 3) {
            finishGame();
            return;
        }
    }
}

function finishGame() {
    isGameOver = true;
    gameOverEl.classList.toggle('hidden');
}

function update(dt) {
    player.update();
    for (let i = 0; i < backgrounds.length; i++) {
        backgrounds[i].update();
    }
    config.speed += config.gameAcceleration;
    updateEnemies();
    updateBullets();
    updatePlatforms();
    checkCollision();
}

function render() {
    ctx.clearRect(0, 0, canvasWidth, canvasHeight);
    for (let i = 0; i < backgrounds.length; i++) {
        renderEntity(backgrounds[i]);
    }

    renderEntities(bullets);
    renderEntity(player.getSprite());
    renderEntity(player.getGunSprite());
    renderEntities(enemies);
    renderEntities(platforms);
    renderScore();
}

function renderScore() {
    if (score != lastScore) {
        if (maxScore < score) {
            maxScore = score;
        }
        scoreEl.textContent = score;
        maxScoreEl.textContent = maxScore;
        lastScore = score;
    }
}

function renderEntities(entities) {
    for (let i = 0; i < entities.length; i++) {
        renderEntity(entities[i].getSprite());
    }
}

function renderEntity(entity) {
    if (entity === undefined) {
        return;
    }

    const {
        image,
        sx,
        sy,
        sWidth,
        sHeight,
        dx,
        dy,
        dWidth,
        dHeight,
        angle,
    } = entity;
    ctx.save();
    let offsetX;
    let offsetY;
    if (angle !== undefined) {
        offsetX = dx;
        offsetY = dy;
        ctx.translate(offsetX, offsetY);
        ctx.rotate(angle);
        ctx.translate(-offsetX, -offsetY);
    }

    if (sx !== undefined) {
        ctx.drawImage(image, sx, sy, sWidth, sHeight, dx, dy, dWidth, dHeight);
    } else {
        if (dWidth !== undefined) {
            ctx.drawImage(image, dx, dy, dWidth, dHeight);
        } else {
            ctx.drawImage(image, dx, dy);
        }
    }

    ctx.restore();
}

function reset() {
    isPause = false;
    isGameOver = false;
    lastShot = 0;
    lastEnemyAppearance = 0;
    player = new Player(canvasWidth, resourcesLoader.get('gun'), resourcesLoader.get('player'), 0);
    player.y = canvasHeight - config.platformHeight;
    enemies = [];
    bullets = [];
    platforms = [];
    createPlatforms();
    score = 0;
    last = performance.now();
    config.speed = defaultSpeed;
}